/*
  <mm-cart>: behavior for the redesigned cart (drawer and cart page).
  All prices and totals come from the server: every change goes through the Cart API with
  the Section Rendering API, and the returned HTML replaces [data-mm-content].
*/

const DEBOUNCE_MS = 300;

class CartRequestError extends Error {}

class MmCart extends HTMLElement {
  connectedCallback() {
    const configElement = this.querySelector(':scope > [data-mm-config]');
    if (!configElement) return;

    this.config = JSON.parse(configElement.textContent);
    this.busy = false;
    this.queued = null;
    this.timer = null;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('change', (event) => this.onChange(event), { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.addEventListener('submit', (event) => this.onSubmit(event), { signal });
    window.addEventListener('pageshow', (event) => event.persisted && this.refresh(), { signal });
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    this.abortController?.abort();
    this.abortController = null;
  }

  get content() {
    return this.querySelector(':scope > [data-mm-content]');
  }

  /* ---------- Events ---------- */

  onClick(event) {
    const target = event.target;

    if (target.closest('[data-mm-close]')) {
      this.closeDrawer();
      return;
    }

    const remove = target.closest('[data-mm-remove]');
    if (remove) {
      event.preventDefault();
      this.queueQuantity(remove.closest('[data-mm-line]'), 0, remove.dataset.focusId);
      return;
    }

    const step = target.closest('[data-mm-step]');
    if (step) {
      const line = step.closest('[data-mm-line]');
      const input = line.querySelector('[data-mm-quantity]');
      this.requestQuantity(line, input, Number(input.value) + Number(step.dataset.mmStep) * Number(input.dataset.step), step.dataset.focusId);
    }
  }

  onChange(event) {
    const target = event.target;

    if (target.matches('[data-mm-quantity]')) {
      const line = target.closest('[data-mm-line]');
      this.requestQuantity(line, target, Number(target.value), target.dataset.focusId);
    } else if (target.matches('[data-mm-swap]')) {
      this.swapVariant(target);
    } else if (target.matches('[data-mm-addon]')) {
      this.toggleAddon(target);
    }
  }

  onSubmit(event) {
    const form = event.target;

    if (form.matches('[data-mm-coupon]')) {
      event.preventDefault();
      this.applyCode(form);
      return;
    }

    // The checkout button submits the cart form natively (quantities are posted with it);
    // block it only while a request is in flight.
    if (this.busy) event.preventDefault();
  }

  /* Enter in a quantity field commits the value; it must not submit the cart form (checkout). */
  onKeydown(event) {
    if (event.key === 'Enter' && event.target.matches('[data-mm-quantity]')) {
      event.preventDefault();
      event.target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  closeDrawer() {
    document.querySelector('cart-drawer')?.close();
  }

  /* ---------- Quantity ---------- */

  requestQuantity(line, input, requested, focusId) {
    const { min, max, step } = {
      min: Number(input.dataset.min) || 1,
      max: input.dataset.max ? Number(input.dataset.max) : Infinity,
      step: Number(input.dataset.step) || 1,
    };
    const current = Number(line.dataset.quantity);

    if (!Number.isFinite(requested) || requested < 0) {
      input.value = current;
      return;
    }
    if (requested === 0) {
      this.queueQuantity(line, 0, focusId);
      return;
    }

    let quantity = Math.max(requested, min);
    if (step > 1) quantity = min + Math.round((quantity - min) / step) * step;

    let message = '';
    if (quantity > max) {
      quantity = max;
      message = this.config.strings.maximum.replace('__q__', max);
    } else if (requested < min) {
      message = this.config.strings.minimum.replace('__q__', min);
    }

    input.value = quantity;
    this.setLineMessage(line, message);
    if (quantity !== current) this.queueQuantity(line, quantity, focusId);
  }

  queueQuantity(line, quantity, focusId) {
    if (this.queued && this.queued.line !== line) this.flushQuantity();
    window.clearTimeout(this.timer);
    this.queued = { line, quantity, focusId };
    this.timer = window.setTimeout(() => this.flushQuantity(), quantity === 0 ? 0 : DEBOUNCE_MS);
  }

  flushQuantity() {
    window.clearTimeout(this.timer);
    const queued = this.queued;
    this.queued = null;
    if (!queued) return Promise.resolve();

    const { line, quantity, focusId } = queued;
    return this.run(
      (post) => post(this.config.changeUrl, { line: Number(line.dataset.line), quantity }),
      { line, focusId }
    );
  }

  /* ---------- Color swap ---------- */

  swapVariant(select) {
    const line = select.closest('[data-mm-line]');
    const key = line.dataset.key;
    const quantity = Number(line.dataset.quantity);
    const variantId = select.value;
    const properties = JSON.parse(line.dataset.properties || '{}');
    const focusId = select.dataset.focusId;

    this.run(
      async (post) => {
        if (Object.keys(properties).length === 0) {
          // One atomic call: remove the old line and add (or top up) the new variant.
          const updates = { [key]: 0 };
          const existing = [...this.querySelectorAll('[data-mm-line]')].find(
            (candidate) =>
              candidate.dataset.variantId === variantId &&
              Object.keys(JSON.parse(candidate.dataset.properties || '{}')).length === 0
          );
          if (existing) updates[existing.dataset.key] = Number(existing.dataset.quantity) + quantity;
          else updates[variantId] = quantity;
          return post(this.config.updateUrl, { updates });
        }

        // The update endpoint cannot carry line properties, so add the new line first and only
        // then remove the old one. If the add fails nothing has changed.
        const added = await post(this.config.addUrl, {
          items: [{ id: Number(variantId), quantity, properties }],
        });
        try {
          // A line's key can change once the cart changes, so the key rendered on the page may be
          // stale by now. Find the old line again in the fresh cart by variant and properties.
          const oldVariantId = Number(line.dataset.variantId);
          const sameProperties = (candidate) => {
            const a = Object.entries(candidate || {}).sort(([x], [y]) => x.localeCompare(y));
            const b = Object.entries(properties).sort(([x], [y]) => x.localeCompare(y));
            return JSON.stringify(a) === JSON.stringify(b);
          };
          let oldKey = key;
          try {
            const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
            const match = cart.items.find(
              (item) => item.variant_id === oldVariantId && item.variant_id !== Number(variantId) && sameProperties(item.properties)
            );
            if (match) oldKey = match.key;
          } catch (lookupError) {
            /* fall back to the rendered key */
          }
          return await post(this.config.changeUrl, { id: oldKey, quantity: 0 });
        } catch (error) {
          error.partial = added;
          throw error;
        }
      },
      { line, focusId }
    );
  }

  /* ---------- Add-on ---------- */

  toggleAddon(input) {
    const key = input.dataset.key;

    if (input.checked && key) return;
    if (!input.checked && !key) return;

    this.run(
      (post) =>
        input.checked
          ? post(this.config.addUrl, { items: [{ id: Number(input.dataset.variantId), quantity: 1 }] })
          : post(this.config.changeUrl, { id: key, quantity: 0 }),
      { focusId: input.dataset.focusId }
    );
  }

  /* ---------- Discount code ---------- */

  applyCode(form) {
    const input = form.querySelector('input[name="discount"]');
    const code = input.value.trim();
    if (!code) {
      this.setCouponFeedback(this.config.strings.couponEmpty);
      return;
    }

    const before = Number(this.querySelector('[data-mm-summary]')?.dataset.totalDiscount) || 0;
    this.run(
      async (post) => {
        const data = await post(this.config.updateUrl, { discount: code });
        data.mmCouponApplied = data.total_discount > before;
        return data;
      },
      { focusId: 'coupon' }
    ).then((data) => {
      if (!data) return;
      this.setCouponFeedback(data.mmCouponApplied ? this.config.strings.couponApplied : this.config.strings.couponNotApplied);
    });
  }

  setCouponFeedback(text) {
    const feedback = this.querySelector('[data-mm-coupon-feedback]');
    if (feedback) feedback.textContent = text;
  }

  /* ---------- Requests ---------- */

  sectionIds() {
    const ids = new Set(
      [...document.querySelectorAll('mm-cart')].map((cart) => cart.dataset.sectionId).filter(Boolean)
    );
    ids.add('cart-icon-bubble');
    return [...ids];
  }

  async post(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        ...body,
        sections: this.sectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json().catch(() => null);

    if (!response.ok || !data || data.status) {
      throw new CartRequestError((data && (data.description || data.message)) || this.config.strings.error);
    }
    return data;
  }

  /* Runs one cart change at a time: locks the UI, renders the result, reports failures. */
  async run(task, { line = null, focusId = null } = {}) {
    if (this.busy) return null;
    this.setBusy(true);

    try {
      const data = await task((url, body) => this.post(url, body));
      this.apply(data, focusId);
      return data;
    } catch (error) {
      if (error.partial) this.apply(error.partial, focusId);
      this.syncQuantities();
      const message = error instanceof CartRequestError ? error.message : this.config.strings.error;
      if (line && line.isConnected) this.setLineMessage(line, message);
      else this.announce(message);
      this.restoreFocus(focusId);
      return null;
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    this.busy = busy;
    this.toggleAttribute('data-busy', busy);
    this.content?.toggleAttribute('inert', busy);
    this.content?.setAttribute('aria-busy', String(busy));
  }

  async refresh() {
    try {
      const response = await fetch(`${window.location.pathname}?sections=${this.sectionIds().join(',')}`);
      const sections = await response.json();
      this.apply({ sections }, null);
    } catch (error) {
      window.location.reload();
    }
  }

  /* ---------- Rendering ---------- */

  apply(data, focusId) {
    const parser = new DOMParser();
    let announcement = '';

    for (const id of this.sectionIds()) {
      const html = data.sections?.[id];
      if (!html) {
        if (id !== 'cart-icon-bubble') {
          window.location.reload();
          return;
        }
        continue;
      }

      const doc = parser.parseFromString(html, 'text/html');
      if (id === 'cart-icon-bubble') {
        const bubble = document.getElementById('cart-icon-bubble');
        const fresh = doc.querySelector('.shopify-section');
        if (bubble && fresh) bubble.innerHTML = fresh.innerHTML;
        continue;
      }

      const freshContent = doc.querySelector('[data-mm-content]');
      if (!freshContent) continue;
      for (const cart of document.querySelectorAll(`mm-cart[data-section-id="${id}"]`)) {
        cart.content?.replaceWith(freshContent.cloneNode(true));
        if (cart === this) announcement = cart.content.querySelector('[data-mm-announce]')?.textContent.trim() || '';
      }
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('is-empty', !this.querySelector('[data-mm-summary]'));

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mm-cart', cartData: data });
    }

    this.announce(announcement);
    this.restoreFocus(focusId);
  }

  restoreFocus(focusId) {
    const target = focusId ? this.querySelector(`[data-focus-id="${focusId}"]:not(:disabled)`) : null;
    (target || this.querySelector('[data-mm-heading]'))?.focus({ preventScroll: true });
  }

  syncQuantities() {
    for (const line of this.querySelectorAll('[data-mm-line]')) {
      const input = line.querySelector('[data-mm-quantity]');
      if (input) input.value = line.dataset.quantity;
    }
  }

  setLineMessage(line, text) {
    const message = line.querySelector('[data-mm-line-message]');
    if (message) message.textContent = text;
  }

  announce(text) {
    const live = this.querySelector(':scope > [data-mm-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => {
      live.textContent = text;
    }, 50);
  }
}

if (!customElements.get('mm-cart')) customElements.define('mm-cart', MmCart);
